import { ApprovalRequestComment } from '@abovo/shared-models';
import { SnackbarNotificationService } from '@abovo/shared-services';
import { ApprovalRequestService } from '@abovo/webvis-data-access';
import { Injectable, inject, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { CommentEditPayload, CommentSubmitPayload, FeedbackCommentNode } from '../components/approval-feedback-comments/approval-feedback-comments.component';

@Injectable()
export class ApprovalFeedbackCommentsService {
   private readonly approvalRequestService = inject(ApprovalRequestService);
   private readonly snackbarService = inject(SnackbarNotificationService);
   private readonly translateService = inject(TranslateService);

   readonly comments = signal<FeedbackCommentNode[]>([]);
   readonly loading = signal(false);

   async load(approvalRequestId: number): Promise<void> {
      this.loading.set(true);
      try {
         const approvalRequest = await firstValueFrom(this.approvalRequestService.get(approvalRequestId));
         this.comments.set(this.buildCommentTree(approvalRequest.comments ?? []));
      } catch (error) {
         console.error('Failed to load comments', error);
         this.comments.set([]);
      } finally {
         this.loading.set(false);
      }
   }

   async add(approvalRequestId: number, payload: CommentSubmitPayload): Promise<void> {
      this.loading.set(true);
      try {
         await firstValueFrom(
            this.approvalRequestService.addComment(approvalRequestId, {
               content: payload.content,
               slideNumber: payload.slideNumber,
               parentId: payload.parentId,
            }),
         );
         await this.load(approvalRequestId);
      } catch (error) {
         this.showMutationError('create');
         throw error;
      } finally {
         this.loading.set(false);
      }
   }

   async edit(approvalRequestId: number, payload: CommentEditPayload): Promise<void> {
      this.loading.set(true);
      try {
         const response = await firstValueFrom(this.approvalRequestService.editComment(approvalRequestId, payload.commentId, { content: payload.content }));
         this.comments.update((tree) => this.replaceInTree(tree, payload.commentId, response.result));
      } catch (error) {
         await this.handleMutationError(error, 'update', approvalRequestId);
         throw error;
      } finally {
         this.loading.set(false);
      }
   }

   async delete(approvalRequestId: number, commentId: number): Promise<void> {
      this.loading.set(true);
      try {
         await firstValueFrom(this.approvalRequestService.deleteComment(approvalRequestId, commentId));
         this.comments.update((tree) => this.buildCommentTree(this.flattenTree(tree).filter((comment) => comment.id !== commentId)));
      } catch (error) {
         await this.handleMutationError(error, 'delete', approvalRequestId);
         throw error;
      } finally {
         this.loading.set(false);
      }
   }

   private showMutationError(action: 'create' | 'update' | 'delete'): void {
      this.snackbarService.showMessage({
         message: this.translateService.instant(`snackbar.${action}.fail`, { subject: 'comment' }),
         extraClasses: ['mat-snack-bar-container--fail'],
      });
   }

   private async handleMutationError(error: unknown, action: 'update' | 'delete', approvalRequestId: number): Promise<void> {
      this.showMutationError(action);
      const status = (error as { status?: number })?.status;
      if (status === 403 || status === 404) {
         await this.load(approvalRequestId);
      }
   }

   /**
    * Builds a render-ready tree of comments from the flat array returned by the API.
    * When a parent is hard-deleted but its replies remain, the orphans are grouped under
    * a synthesized tombstone node (isTombstone: true) so the existing nested-render
    * template can show them with a "[deleted]" placeholder header.
    */
   private buildCommentTree(input: ApprovalRequestComment[]): FeedbackCommentNode[] {
      // Walk any pre-existing nesting so we work on both the API's nested payload and the flat output of flattenTree.
      const flatten = (items: ApprovalRequestComment[]): ApprovalRequestComment[] => items.flatMap((item) => [item, ...flatten(item.children ?? [])]);

      // Dedupe by id (the API echoes each reply both at the top level and nested) and reset children so we can re-link from scratch.
      const byId = new Map(flatten(input).map((node) => [node.id, { ...node, children: [] as ApprovalRequestComment[] }]));
      const allNodes = [...byId.values()];

      // Attach every non-root node to its parent's children array via parent_id.
      allNodes.forEach((node) => {
         if (node.parent_id) byId.get(node.parent_id)?.children.push(node);
      });

      const roots = allNodes.filter((node) => !node.parent_id);
      // Orphans: replies whose parent was hard-deleted server-side. Group them so we can show one tombstone per missing parent.
      const orphans = allNodes.filter((node): node is typeof node & { parent_id: number } => !!node.parent_id && !byId.has(node.parent_id));
      const orphansByParent = Map.groupBy(orphans, (node) => node.parent_id);

      // Synthesize a "[deleted]" placeholder parent for each orphan group so the existing nested-render template can show them.
      const tombstones: FeedbackCommentNode[] = [...orphansByParent.entries()].map(([parentId, children]) => ({
         id: parentId,
         approval_request_id: children[0].approval_request_id,
         content: '',
         slide_number: children[0].slide_number,
         parent_id: null,
         create_date_time: new Date(Math.min(...children.map((child) => child.create_date_time.getTime()))),
         children,
         isTombstone: true,
      }));

      return [...roots, ...tombstones];
   }

   /** Recursively replaces a comment in the tree with `updated`, preserving children where present. */
   private replaceInTree(tree: FeedbackCommentNode[], targetId: number, updated: ApprovalRequestComment): FeedbackCommentNode[] {
      return tree.map((node) => {
         if (node.id === targetId && !node.isTombstone) {
            return { ...node, ...updated, children: node.children };
         }
         if (node.children?.length) {
            return { ...node, children: this.replaceInTree(node.children, targetId, updated) };
         }
         return node;
      });
   }

   /** Flattens the tree back to the API shape, dropping synthesized tombstone wrappers but keeping their children. */
   private flattenTree(tree: FeedbackCommentNode[]): ApprovalRequestComment[] {
      return tree.flatMap((node) => {
         if (node.isTombstone) {
            return this.flattenTree(node.children ?? []);
         }
         const self: ApprovalRequestComment = {
            id: node.id,
            approval_request_id: node.approval_request_id,
            content: node.content,
            slide_number: node.slide_number,
            parent_id: node.parent_id,
            create_date_time: node.create_date_time,
            commentor: node.commentor,
            children: [],
         };
         return [self, ...this.flattenTree(node.children ?? [])];
      });
   }
}
